<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import V2ReservationsTimelineRow from './V2ReservationsTimelineRow.vue';
  import { buildTimelineLayout } from '@/domain/reservations/reservation-timeline';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import type {
    IReservation,
    IReservationDayShift,
  } from '@/interfaces/IReservation';
  const props = defineProps<{
    date: string;
    reservations: IReservation[];
    shifts: IReservationDayShift[];
    now: Date;
    timeZone: string;
    toleranceMin: number;
    businessDayStartHour?: number;
    selectedId: number | null;
  }>();
  const emit = defineEmits<{
    (event: 'select', id: number): void;
    (event: 'create', value: { tableIds: number[]; startsAt: string }): void;
  }>();
  const restaurant = useRestaurantStore();
  const layout = computed(() =>
    buildTimelineLayout({
      date: props.date,
      timeZone: props.timeZone,
      now: props.now,
      toleranceMin: props.toleranceMin,
      businessDayStartHour: props.businessDayStartHour,
      shifts: props.shifts,
      tables: restaurant.tables ?? [],
      sectors: restaurant.sectors ?? [],
      reservations: props.reservations.filter(
        (row) => row.status !== 'cancelled' && row.status !== 'no_show'
      ),
    })
  );
  const marker = computed(() => ({
    left: `calc(8rem + (100% - 8rem) * ${
      (layout.value.nowColumn ?? 0) / layout.value.columns.length
    })`,
  }));
  // The "now" chip lives in the heading grid so the sticky table column can cover it.
  const nowLabel = computed(() => {
    const column = layout.value.nowColumn ?? 0;
    return {
      gridColumn: String(
        Math.min(Math.floor(column), layout.value.columns.length - 1) + 2
      ),
      gridRow: '1',
      marginLeft: `${(column % 1) * 100}%`,
    };
  });
</script>

<template>
  <div class="reservations-timeline">
    <PosUiEmptyState
      v-if="!layout.rows.length"
      icon="table-chair"
      :title="$t('reservations.timeline.emptyTitle')"
      :description="$t('reservations.timeline.emptyDescription')"
    >
      <template #action
        ><PosUiButton
          color="neutral"
          variant="outline"
          @click="$router.push(`/${restaurant.restaurant.stringId}/tables`)"
          >{{ $t('reservations.tables.manage') }}</PosUiButton
        ></template
      >
    </PosUiEmptyState>
    <PosUiScrollable
      v-else
      orientation="horizontal"
      hide-scrollbar="touch"
      arrows
      class="timeline-scroll"
    >
      <div
        class="timeline-grid"
        :style="{ '--timeline-columns': layout.columns.length }"
      >
        <div class="timeline-heading">
          <div class="table-heading">
            {{ $t('reservations.timeline.table') }}
          </div>
          <time
            v-for="(column, index) in layout.columns"
            :key="column.startsAt"
            :datetime="column.startsAt"
            :class="{ hour: column.hour }"
            :style="{ gridColumn: String(index + 2), gridRow: '1' }"
            >{{ column.hour ? column.label : '' }}</time
          >
          <span
            v-if="layout.nowColumn !== null"
            class="now-label"
            :style="nowLabel"
            >{{ $t('reservations.timeline.now') }}</span
          >
        </div>
        <V2ReservationsTimelineRow
          v-for="row in layout.rows"
          :key="row.table.id"
          :row="row"
          :columns="layout.columns"
          :selected-id="selectedId"
          @select="emit('select', $event)"
          @create="emit('create', $event)"
        />
        <div
          v-if="layout.nowColumn !== null"
          class="now-marker"
          :style="marker"
        />
      </div>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .reservations-timeline {
    @apply tw-h-full tw-min-h-0 tw-bg-white;
    .timeline-scroll {
      @apply tw-h-full;
      :deep(.scroll-arrow.scroll-arrow-left) {
        left: calc(8rem + 0.375rem);
      }
      &.overlay :deep(.gradient-left) {
        left: 8rem;
      }
    }
    .timeline-grid {
      --timeline-cell: 44px;
      @apply tw-relative tw-min-h-full;
      min-width: calc(8rem + var(--timeline-columns) * var(--timeline-cell));
    }
    .timeline-heading {
      @apply tw-sticky tw-top-0 tw-z-20 tw-grid tw-min-h-11 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-500;
      grid-template-columns: 8rem repeat(
          var(--timeline-columns),
          minmax(var(--timeline-cell), 1fr)
        );
      > time {
        @apply tw-flex tw-items-center tw-pl-1;
      }
      .now-label {
        @apply tw-pointer-events-none tw-z-10 tw-self-end tw-justify-self-start tw-rounded tw-bg-red-500 tw-px-1 tw-text-[10px] tw-font-medium tw-text-white;
        margin-bottom: -1.25rem;
      }
    }
    .table-heading {
      @apply tw-sticky tw-left-0 tw-z-40 tw-flex tw-items-center tw-border-0 tw-border-r tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-3;
    }
    .now-marker {
      @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-top-0 tw-z-[15] tw-w-px tw-bg-red-500;
    }
    @media (max-width: 767px) {
      .timeline-grid {
        --timeline-cell: 40px;
      }
    }
  }
</style>
